import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, NEVER, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthHttpInterceptor, AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '../services/config.service';

// Auth0 SDK errors after which only a new login helps
const SESSION_LOST_ERRORS = [
  'invalid_grant',
  'missing_refresh_token',
  'login_required',
  'consent_required',
];

// Adds the Auth0 token to our API requests and sends the user to log in again when the session is lost
@Injectable()
export class Auth0Interceptor extends AuthHttpInterceptor {
  private auth = inject(AuthService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private reLoginStarted = false;

  WHITELISTED_URLS: string[] = [
    'api/dict',
    'api/version',
    'auth-api/auth/verify-email',
    'assets/i18n',
  ];

  private isWhitelisted(url: string): boolean {
    return this.WHITELISTED_URLS.some((whitelisted: string) => url.includes(whitelisted));
  }

  override intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.isWhitelisted(request.url)) return next.handle(request);
    // The page is leaving for the Auth0 login, further requests would only fail
    if (this.reLoginStarted) return NEVER;

    return super.intercept(request, next).pipe(
      catchError((error) => {
        console.warn(
          `[Auth0Interceptor] ${typeof error == 'object' ? JSON.stringify(error) : error}`,
        );
        // A cached user means the session was lost - log in again
        if (!(error instanceof HttpErrorResponse) && SESSION_LOST_ERRORS.includes(error?.error)) {
          return this.auth.isAuthenticated$.pipe(
            take(1),
            switchMap((loggedIn) => (loggedIn ? this.reLogin() : throwError(() => error))),
          );
        }
        throw error;
      }),
    );
  }

  // Clears the local session and logs in again, back to the current page
  private reLogin(): Observable<never> {
    if (!this.reLoginStarted) {
      this.reLoginStarted = true;
      this.auth
        .logout({ openUrl: false })
        .pipe(
          switchMap(() =>
            this.auth.loginWithRedirect({
              appState: { target: this.router.url },
              authorizationParams: {
                ui_locales: ConfigService.getAuthUiLocale(this.translate.currentLang),
              },
            }),
          ),
        )
        .subscribe();
    }
    return NEVER;
  }
}
